// src/posts/is-it-perimenopause.js

export const meta = {
  slug: 'is-it-perimenopause',
  title: 'Is it perimenopause — or are you imagining it?',
  date: '2026-05-27',
  readTime: '4 min read',
  description: 'Brain fog, rage, hot flashes, aches and pains — and nobody taking you seriously. If this sounds familiar, you are not imagining it. Here is what might actually be going on.',
};

export const content = `
Is it perimenopause — or are you imagining it?

You're in your late 30s or 40s and something has shifted. You can't quite put your finger on it, but you know your body and your mind don't feel like they used to.

Maybe it's the brain fog that descends without warning — you're mid-sentence and the word just disappears. Maybe it's the rage that comes from nowhere, disproportionate and alarming, over something small. Maybe it's waking at 3am, drenched in sweat, staring at the ceiling with your heart racing for no reason. Or the joint aches. The bloating. The anxiety that wasn't there before.

And then you wonder — is this just stress? Am I tired? Is this just getting older?

You're not imagining it.

What nobody tells you about perimenopause

Perimenopause — the transition period before menopause — can begin as early as your mid-30s. It's not a sudden switch. It's a gradual hormonal shift that can take years, and it looks different for every woman.

The symptoms are wide-ranging and often dismissed. Brain fog gets written off as tiredness. Rage gets labelled as anxiety or stress. Digestive issues get attributed to diet. Hot flashes get missed entirely because women in their late 30s aren't supposed to have them yet.

And when women do bring these symptoms to their GP, they're often told they're too young for perimenopause. They're offered antidepressants. They're told to manage their stress better.

It's one of the most frustrating experiences in women's healthcare — knowing something is wrong, not being able to explain it clearly, and being sent home without answers.

The problem isn't just the symptoms. It's the disconnection.

Here's what makes perimenopause so hard to articulate. The symptoms don't always feel connected. You might not link the terrible sleep last Tuesday to the anxiety on Wednesday, or the fact that your worst weeks seem to follow a pattern you can't quite see yet.

Your GP gets ten minutes with you. You forget half of what you meant to say. You leave feeling dismissed, and you still don't have answers.

This is exactly the gap that ApptNote was built to fill.

You are not going mad. You just need evidence.

When you track your symptoms daily — even for just a few weeks — patterns start to emerge that are invisible in the moment. Sleep and mood are connected. Stress and hot flashes are connected. Your symptoms might cluster around certain points in your cycle in ways that point clearly toward hormonal shifts.

ApptNote keeps your daily logs in one place and uses AI to pull them into a clear summary, in your own words, that you can bring to your doctor.

Not vague feelings. Not "I've been a bit off lately." Real data, real patterns, real evidence that you are not imagining it.

Because you deserve to walk into that appointment prepared. You deserve to be taken seriously. And you deserve answers.

If any of this sounds familiar, start tracking today. It takes two minutes a day. And it might be the thing that finally gets you heard.
`;
